import type { GroupProductByNameContext } from '~/stores/products/types'
import type { ProductCategoryContext, ProductContext } from '~/types'

export type ProductReturnResponse = {
	groupedByName?: GroupProductByNameContext
	productList?: ProductContext
}
export type ProductCategoryReturnReponse = {
	productCategories: ProductCategoryContext
	groupedProducts: Record<string, ProductContext>
}
export type WPCategoryResponse = Array<{
	id: number
	parent: number
	description?: string
	name: string
	count?: number
	slug?: string
}>
export type WPPostResponse = Array<{
	id: number
	date: string
	slug: string
	count?: number
	categories: number[] | WPCategoryResponse
	tags: number[] | WPCategoryResponse
	title: {
		rendered: string
	}
	content: {
		rendered: string
		protected: boolean
	}
	excerpt: {
		rendered: string
		protected: boolean
	}
	yoast_head_json: {
		title: string
		robots: {
			index: string
			follow: string
			'max-snippet': string
			'max-image-preview': string
			'max-video-preview': string
		}
		og_locale: string
		og_type: string
		og_title: string
		og_description: string
		og_url: string
		og_site_name: string
		article_publisher: string
		article_published_time: string
		article_modified_time: string
		og_image: Array<{
			width: number
			height: number
			url: string
			type: string
		}>
		author: string
		twitter_card: string
		twitter_creator: string
		twitter_site: string
		twitter_misc: {
			'Written by': string
			'Est. reading time': string
		}
		schema: {
			'@context': string
			'@graph': Array<{
				'@type': string
				'@id': string
				url?: string
				name?: string
				isPartOf?: {
					'@id': string
				}
				primaryImageOfPage?: {
					'@id': string
				}
				image?: {
					'@type'?: string
					inLanguage?: string
					'@id': string
					url?: string
					contentUrl?: string
					caption?: string
				}
				thumbnailUrl?: string
				datePublished?: string
				dateModified?: string
				author?: {
					'@id': string
				}
				breadcrumb?: {
					'@id': string
				}
				inLanguage?: string
				potentialAction?: Array<{
					'@type': string
					target: any
					'query-input'?: {
						'@type': string
						valueRequired: boolean
						valueName: string
					}
				}>
				contentUrl?: string
				width?: number
				height?: number
				itemListElement?: Array<{
					'@type': string
					position: number
					name: string
					item?: string
				}>
				description?: string
				sameAs?: Array<string>
			}>
		}
	}
	_links: {
		self: Array<{
			href: string
			targetHints: {
				allow: Array<string>
			}
		}>
		collection: Array<{
			href: string
		}>
		about: Array<{
			href: string
		}>
		author: Array<{
			embeddable: boolean
			href: string
		}>
		replies: Array<{
			embeddable: boolean
			href: string
		}>
		'version-history': Array<{
			count: number
			href: string
		}>
		'predecessor-version': Array<{
			id: number
			href: string
		}>
		'wp:featuredmedia': Array<{
			embeddable: boolean
			href: string
		}>
		'wp:attachment': Array<{
			href: string
		}>
		'wp:term': Array<{
			taxonomy: string
			embeddable: boolean
			href: string
		}>
		curies: Array<{
			name: string
			href: string
			templated: boolean
		}>
	}
	_embedded: {
		author: Array<{
			id: number
			name: string
			url: string
			description: string
			link: string
			slug: string
			avatar_urls: {
				'24': string
				'48': string
				'96': string
			}
			yoast_head: string
			yoast_head_json: {
				title: string
				robots: {
					index: string
					follow: string
					'max-snippet': string
					'max-image-preview': string
					'max-video-preview': string
				}
				og_locale: string
				og_type: string
				og_title: string
				og_url: string
				og_site_name: string
				og_image: Array<{
					url: string
					type: string
					width: string
					height: string
				}>
				twitter_card: string
				twitter_site: string
				schema: {
					'@context': string
					'@graph': Array<{
						'@type': string
						'@id': string
						url?: string
						name?: string
						isPartOf?: {
							'@id': string
						}
						breadcrumb?: {
							'@id': string
						}
						inLanguage?: string
						potentialAction?: Array<{
							'@type': string
							target: any
							'query-input'?: {
								'@type': string
								valueRequired: boolean
								valueName: string
							}
						}>
						itemListElement?: Array<{
							'@type': string
							position: number
							name: string
							item?: string
						}>
						description?: string
						image?: {
							'@type': string
							inLanguage: string
							'@id': string
							url: string
							contentUrl: string
							caption: string
						}
						sameAs?: Array<string>
						mainEntityOfPage?: {
							'@id': string
						}
					}>
				}
			}
			_links: {
				self: Array<{
					href: string
					targetHints: {
						allow: Array<string>
					}
				}>
				collection: Array<{
					href: string
				}>
			}
		}>
		replies: Array<
			Array<{
				id: number
				parent: number
				author: number
				author_name: string
				author_url: string
				date: string
				content: {
					rendered: string
				}
				link: string
				type: string
				author_avatar_urls: {
					'24': string
					'48': string
					'96': string
				}
				_links: {
					self: Array<{
						href: string
						targetHints: {
							allow: Array<string>
						}
					}>
					collection: Array<{
						href: string
					}>
					up: Array<{
						embeddable: boolean
						post_type: string
						href: string
					}>
				}
			}>
		>
		'wp:featuredmedia': Array<{
			id: number
			date: string
			slug: string
			type: string
			link: string
			title: {
				rendered: string
			}
			author: number
			featured_media: number
			caption: {
				rendered: string
			}
			alt_text: string
			media_type: string
			mime_type: string
			media_details: {
				width: number
				height: number
				file: string
				filesize: number
				sizes: {
					medium: {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					large: {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					thumbnail: {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					medium_large: {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					'1536x1536': {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					'2048x2048': {
						file: string
						width: number
						height: number
						filesize: number
						mime_type: string
						source_url: string
					}
					full: {
						file: string
						width: number
						height: number
						mime_type: string
						source_url: string
					}
				}
				image_meta: {
					aperture: string
					credit: string
					camera: string
					caption: string
					created_timestamp: string
					copyright: string
					focal_length: string
					iso: string
					shutter_speed: string
					title: string
					orientation: string
					keywords: Array<any>
				}
			}
			source_url: string
			_links: {
				self: Array<{
					href: string
					targetHints: {
						allow: Array<string>
					}
				}>
				collection: Array<{
					href: string
				}>
				about: Array<{
					href: string
				}>
				author: Array<{
					embeddable: boolean
					href: string
				}>
				replies: Array<{
					embeddable: boolean
					href: string
				}>
			}
		}>
		'wp:term': Array<
			Array<{
				id: number
				link: string
				name: string
				slug: string
				count?: number
				taxonomy: string
				yoast_head: string
				yoast_head_json: {
					title: string
					robots: {
						index: string
						follow: string
						'max-snippet': string
						'max-image-preview': string
						'max-video-preview': string
					}
					og_locale: string
					og_type: string
					og_title: string
					og_url: string
					og_site_name: string
					twitter_card: string
					twitter_site: string
					schema: {
						'@context': string
						'@graph': Array<{
							'@type': string
							'@id': string
							url?: string
							name?: string
							isPartOf?: {
								'@id': string
							}
							breadcrumb?: {
								'@id': string
							}
							inLanguage?: string
							itemListElement?: Array<{
								'@type': string
								position: number
								name: string
								item?: string
							}>
							description?: string
							potentialAction?: Array<{
								'@type': string
								target: {
									'@type': string
									urlTemplate: string
								}
								'query-input': {
									'@type': string
									valueRequired: boolean
									valueName: string
								}
							}>
						}>
					}
				}
				_links: {
					self: Array<{
						href: string
						targetHints: {
							allow: Array<string>
						}
					}>
					collection: Array<{
						href: string
					}>
					about: Array<{
						href: string
					}>
					'wp:post_type': Array<{
						href: string
					}>
					curies: Array<{
						name: string
						href: string
						templated: boolean
					}>
				}
			}>
		>
	}
}>

export type BlogQuery = Partial<{
	context: 'view' | 'embed' | 'edit'
	page: number
	per_page: number
	search: string
	exclude: Array<number>
	include: Array<number>
	offset: number
	categories_exclude: Array<number>
	order: 'asc' | 'desc'
	orderby: 'author' | 'date' | 'id' | 'include' | 'title' | 'slug' | 'term_group' | 'description' | 'count'
	hide_empty: boolean
	categories?: number | string
	tags?: number
	before?: string
	after?: string
	date?: string
	_fields?: string
}>
export type BlogPostContext = {
	id: number
	date: string
	title: string
	content: string
	tags: Array<string>
	categories: WPCategoryResponse
	excerpt: string
	slug: string
	count?: number
	media: {
		src: string
		thumbnails: {
			file: string
			width: number
			height: number
			filesize: number
			mime_type: string
			source_url: string
		}
	}
	author: Array<{
		id: number
		name: string
		description: string
		slug: string
		avatar_urls: {
			'24': string
			'48': string
			'96': string
		}
	}>
	seo: {
		robots: {
			index: string
			follow: string
			'max-snippet': string
			'max-image-preview': string
			'max-video-preview': string
		}
		title: string
		og_type: string
		og_title: string
		og_description: string
		article_publisher: string
		article_published_time: string
		article_modified_time: string
		author: string
		twitter_card: string
		twitter_creator: string
		twitter_site: string
	}
}
export enum ProductPlatforms {
	BUSINESS = 'business',
	INDIVIDUAL = 'individual',
	DIASPORA = 'diaspora'
}
export type ProductPlatformKeys = 'business' | 'individual' | 'diaspora'
export type ProductGroupId = 'flexicare' | 'primecare' | 'zencare' | 'senior' | ''
export const PlatformValues = Object.values(ProductPlatforms)

export enum HealthProviders {
	HYGEIA = 'hygeia',
	BASTION = 'bastion'
}

export interface BenefitsQuery {
	is_business_staff?: boolean | string
	user_type: ProductPlatformKeys
	category: string
	group_id: ProductGroupId
	code_name?: number
	provider?: HealthProviders
}
export interface ExportPayload {
	file_name: string
	items: Record<string, unknown>[]
	column?: string
	dataIndex?: string | Record<string, string>
}
